/**
 * @file
 * An OkCupid API client that works on all platforms.
 */

import isApp from "shared/util/isApp";
import apiRequest from "./internal";
import { METHODS } from "./constants";

// on the app, if we're making calls before the access token is updated,
// cache that shit / store it / smoke it later
let _requestBuffer = [];
let _requestBufferTimeout = null;

function _isReady() {
	return !isApp() || window.App.access_token_updated;
}

function _checkApi() {
	if (_isReady()) {
		_requestBuffer.forEach(({
			path, params, resolve, reject,
		}) => {
			/* eslint-disable */
			OkC.api(path, params).then(resolve).catch(reject);
			/* eslint-enable */
		});
		_requestBuffer = [];
	} else {
		clearTimeout(_requestBufferTimeout);
		_requestBufferTimeout = setTimeout(_checkApi, 100);
	}
}

function _addToBuffer(path, params = {}, resolve, reject) {
	_requestBuffer.push({
		path, params, resolve, reject,
	});
	_checkApi();
}


const OkC = {
	/**
	 * The main request method for the API.
	 * @param {string} path - The API path to request agains, i.e. "/profile"
	 * @param {object} params - A dictionary of params for the request
	 * @param {object} params.data - What data to send to the endpoint.
	 * @param {string} params.type - What HTTP method to use for the request.
	 * @param {integer} params.version - What endpoint version to use.
	 * @param {object} params.headers - Any additional headers to include.
	 * @param {function} params.success - A function to call after success. Only
	 *   for legacy purposes, use promises `then` wherever possible.
	 * @param {function} params.error - A function to call after error. Only for
	 *   legacy purposes, use promises `catch` wherever possible.
	 *
	 * @returns {Promise}
	 */
	api(path, params = {}) {
		if (_isReady()) {
			return apiRequest(path, params);
		} else {
			return new Promise((resolve, reject) => {
				_addToBuffer(path, params, resolve, reject);
			});
		}
	},

	/**
	 * Shortcut for api that sets the HTTP method to GET.
	 * @param {string} path - The API path to request agains, i.e. "/profile"
	 * @param {object} params - A dictionary of params for the request
	 * @returns {Promise}
	 * @see {@link OkC.api}
	 */
	get(path, params = {}) {
		return this.api(path, { ...params, type: METHODS.GET });
	},

	/**
	 * Shortcut for api that sets the HTTP method to POST.
	 * @param {string} path - The API path to request agains, i.e. "/profile"
	 * @param {object} params - A dictionary of params for the request
	 * @returns {Promise}
	 * @see {@link OkC.api}
	 */
	post(path, params = {}) {
		return this.api(path, { ...params, type: METHODS.POST });
	},

	/**
	 * Shortcut for api that sets the HTTP method to PUT.
	 * @param {string} path - The API path to request agains, i.e. "/profile"
	 * @param {object} params - A dictionary of params for the request
	 * @returns {Promise}
	 * @see {@link OkC.api}
	 */
	put(path, params = {}) {
		return this.api(path, { ...params, type: METHODS.PUT });
	},

	/**
	 * Shortcut for api that sets the HTTP method to DELETE.
	 * @param {string} path - The API path to request agains, i.e. "/profile"
	 * @param {object} params - A dictionary of params for the request
	 * @returns {Promise}
	 * @see {@link OkC.api}
	 */
	delete(path, params = {}) {
		return this.api(path, { ...params, type: METHODS.DELETE });
	},

	/**
	 * For old endpoints that aren't on the API, use this function.
	 * @param {string} path - The path to hit.
	 * @param {object} params - A dictionary of params for the request.
	 * @param {object} data - What data to send to the endpoint.
	 * @param {string} type - What HTTP method to use for the request.
	 * @param {object} headers - Any HTTP headers you want to enforce.
	 * @returns {Promise}
	 */
	legacy(path, params = {}) {
		return apiRequest(path, { ...params, isLegacy: true });
	},
};

export default OkC;



// WEBPACK FOOTER //
// ./src/shared/util/OkcApi/index.js